import { Injectable } from "@angular/core";
import { map, take } from "rxjs/operators";
import {
  ApiAction,
  ApiAddAction,
  ApiBrowseAction,
  ApiDeleteAction,
  ApiEditAction
} from "../../api-actions";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
import { ApiSessionService } from "../../api-session.service";
import { ApiStateStoreService } from "../../api-state-store.service";
import { domain } from "../../apiParams/domain";
import { organizationId } from "../../apiParams/organization-id";
import { sessionId } from "../../apiParams/session-id";
import { extractData, OnsipApiResponse } from "../../apiResponse/response-body-new";
import { onsipApiArrayToArray } from "../../apiResponse/xml-json";
import { getApiActionName } from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { ApiResourceService } from "../api-resource.service";
import {
  ApiExternalAddress,
  ExternalAddress,
  apiExternalAddressToExternalAddress as clean
} from "./external-address";

const debug = false;

@Injectable({ providedIn: "root" })
export class ExternalAddressService extends ApiResourceService<ExternalAddress> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    super(session, store, promiseState, "ExternalAddress", "addressId");
    debug && this.state.subscribe(state => console.warn("ExternalAddressService", state));
  }

  externalAddressBrowse(Limit: number): ApiPromiseState<ExternalAddress> {
    this.dispatcher.next({
      parameters: {
        Action: ApiBrowseAction.ExternalAddressBrowse,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Limit
      }
    });
    return this.promiseState.toPromise(ApiBrowseAction.ExternalAddressBrowse);
  }

  externalAddressAdd(params: {
    ForeignAddress: string;
    Username: string;
    Name: string;
  }): ApiPromiseState<ExternalAddress> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAddAction.ExternalAddressAdd,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Domain: this.store.state.pipe(domain()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiAddAction.ExternalAddressAdd);
  }

  externalAddressEdit(params: {
    ForeignAddress: string;
    Address: string;
    Name: string;
    Username: string;
  }): ApiPromiseState<ExternalAddress> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.ExternalAddressEdit,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiEditAction.ExternalAddressEdit);
  }

  externalAddressDelete(Address: string): ApiPromiseState<ExternalAddress> {
    this.dispatcher.next({
      parameters: {
        Action: ApiDeleteAction.ExternalAddressDelete,
        Address,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId())
      }
    });

    return this.promiseState.toPromise(ApiDeleteAction.ExternalAddressDelete);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    switch (action) {
      case ApiAction.ExternalAddressBrowse:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            extractData<Array<ApiExternalAddress>>(
              response,
              action,
              "ExternalAddress",
              "ExternalAddresses"
            ).map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiAction.ExternalAddressAdd:
      case ApiAction.ExternalAddressEdit:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            [clean(extractData<ApiExternalAddress>(response, action, "ExternalAddress"))],
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiAction.ExternalAddressDelete:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const deletedAddress = onsipApiArrayToArray(
          response.Context.Request.Parameters,
          "Parameter"
        ).find(param => param.Name === "Address")?.Value;
        if (deletedAddress) {
          this.state
            .pipe(
              map(state => Object.values(state.state)),

              take(1)
            )
            .subscribe(externalAddresses => {
              const externalAddress = externalAddresses.find(
                address => address.aor === deletedAddress
              );

              if (externalAddress) {
                this.store.mergeStateUpdate(
                  this.resourceName,
                  {
                    [externalAddress.addressId]: undefined
                  },
                  action
                );
              }
            });
        }
        break;
    }
  }
}
